import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { X, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";

const SESSION_KEY = "tj_popup_dismissed";

const DEFAULTS = {
  enabled: true,
  delay_ms: 4000,
};

export default function EarlyAccessModal() {
  const [settings, setSettings] = useState(DEFAULTS);
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (_) {}
    setTimeout(() => setMounted(false), 250);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let showTimer = null;

    async function init() {
      let s = DEFAULTS;
      try {
        const { data } = await axios.get(`${API}/popup-settings`);
        if (data && typeof data === "object") s = { ...DEFAULTS, ...data };
      } catch (_) {}
      if (cancelled) return;
      setSettings(s);

      if (!s.enabled) return;
      try {
        if (sessionStorage.getItem(SESSION_KEY) === "1") return;
      } catch (_) {}

      const delay = Math.min(Math.max(Number(s.delay_ms) || 4000, 0), 30000);
      showTimer = setTimeout(() => {
        if (cancelled) return;
        setMounted(true);
        requestAnimationFrame(() => setOpen(true));
      }, delay);
    }

    init();
    return () => {
      cancelled = true;
      if (showTimer) clearTimeout(showTimer);
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mounted, close]);

  useEffect(() => {
    if (!mounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mounted]);

  if (!mounted) return null;

  return (
    <div
      data-testid="early-access-modal-root"
      aria-hidden={!open}
      role="dialog"
      aria-modal="true"
      aria-label="T&J Clinical Solutions — Coming Soon"
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 transition-opacity duration-300 ${
        open ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close popup"
        data-testid="early-access-backdrop"
        onClick={close}
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm"
      />

      {/* Card */}
      <div
        data-testid="early-access-card"
        className={`relative w-full max-w-md sm:max-w-lg bg-white rounded-2xl overflow-hidden shadow-2xl ring-1 ring-slate-200 transform transition-all duration-300 ${
          open ? "translate-y-0 scale-100" : "translate-y-3 scale-[0.98]"
        }`}
      >
        {/* Close button */}
        <button
          type="button"
          aria-label="Close"
          data-testid="early-access-close-button"
          onClick={close}
          className="absolute top-3 right-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/85 hover:bg-white text-slate-700 hover:text-slate-900 border border-slate-200 transition-colors shadow-sm"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Hero image — the entire visual */}
        <div className="relative bg-white" data-testid="early-access-image-wrap">
          <img
            src="/tj-coming-soon.png"
            alt="T&J Clinical Solutions — Coming Soon. Care is in Motion."
            className="block w-full h-auto select-none"
            draggable={false}
          />
        </div>

        {/* CTA bar */}
        <div className="bg-white px-5 sm:px-7 py-5 sm:py-6 border-t border-slate-100">
          <Button
            type="button"
            onClick={close}
            data-testid="early-access-explore-button"
            className="w-full h-12 rounded-lg bg-brand-teal hover:bg-brand-teal-hover text-white font-semibold tracking-wide text-sm sm:text-base"
          >
            Explore the Site <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          <p className="mt-3 text-center text-[10px] uppercase tracking-[0.22em] text-slate-400">
            T&amp;J Clinical Solutions — Healthcare Support Services
          </p>
        </div>
      </div>
    </div>
  );
}
